import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { notify } from '@/components/base/notifications/notifications';
import { BASE_PATH } from '@/constants/config';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';

import { loginDestinationForRoles } from './login';

const RESEND_WAIT_MS = 60 * 1000;

class RateLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RateLimitError';
  }
}

export const Route = createFileRoute('/forgot-password')({
  validateSearch: z.object({ email: z.string().optional() }),
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: loginDestinationForRoles(context.user.roles) });
    }
  },
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useLingui();
  const analytics = useAnalytics();
  const search = Route.useSearch();
  const [lastResend, setLastResend] = useState<number>();
  const [submittedEmail, setSubmittedEmail] = useState<string>();

  const forgotPasswordSchema = z.object({
    email: z
      .email(t`Please enter a valid email address`)
      .trim()
      .min(1, t`Email is required`),
  });

  type FormValues = z.infer<typeof forgotPasswordSchema>;
  const mutation = useMutation({
    mutationFn: async ({ email }: FormValues) => {
      const [, response] = await Promise.all([
        new Promise((resolve) => setTimeout(resolve, 1000)),
        fetch(`${BASE_PATH}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }),
      ]);

      if (response.ok) return;

      if (response.status === 429) {
        throw new RateLimitError(t`Too many attempts. Please try again later.`);
      }

      throw new Error(t`Failed to request password reset`);
    },
    onSuccess: (_, variables) => {
      setSubmittedEmail(variables.email);
      setLastResend(Date.now());
      analytics?.track({ action: 'requested', actionSubject: 'passwordReset' });
      notify({ color: 'success', title: t`Email sent` });
    },
    onError: (error) => {
      if (error instanceof RateLimitError) {
        notify({
          color: 'error',
          title: t`Too many attempts. Please try again later.`,
        });
      }
    },
  });
  const shouldShowGenericError =
    mutation.isError && !(mutation.error instanceof RateLimitError);

  const form = useForm({
    defaultValues: { email: search.email ?? '' },
    validators: { onSubmit: forgotPasswordSchema },
    onSubmit: ({ value }) => mutation.mutateAsync(value),
  });

  if (submittedEmail) {
    return (
      <AuthLayout>
        <CheckEmailConfirmation email={submittedEmail}>
          <ResendButton
            isPending={mutation.isPending}
            lastResend={lastResend}
            onClick={() => mutation.mutate({ email: submittedEmail })}
          />
        </CheckEmailConfirmation>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Forgot password?</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              Enter the email you used to sign up and we&apos;ll send you a link
              to reset your password.
            </Trans>
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <FormInput
            type="email"
            form={form}
            field="email"
            label={t`Email`}
            placeholder={t`Enter your email`}
            isRequired
            size="md"
          />

          {shouldShowGenericError && (
            <p className="text-sm text-error-primary">
              <Trans>An unexpected error occurred. Please try again.</Trans>
            </p>
          )}

          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting || mutation.isPending}
                isDisabled={isSubmitting || mutation.isPending}
                className="w-full"
              >
                <Trans>Send reset link</Trans>
              </Button>
            )}
          />
        </div>

        <p className="text-sm text-tertiary">
          <Link
            to="/login"
            className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
          >
            <Trans>Back to log in</Trans>
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

function ResendButton({
  isPending,
  lastResend,
  onClick,
}: {
  isPending: boolean;
  lastResend?: number;
  onClick: () => void;
}) {
  const [cooldownEndsAt, setCooldownEndsAt] = useState(
    () => Date.now() + RESEND_WAIT_MS,
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (lastResend) {
      setCooldownEndsAt(lastResend + RESEND_WAIT_MS);
    }
  }, [lastResend]);

  useEffect(() => {
    if (cooldownEndsAt <= Date.now()) return;

    const intervalId = globalThis.setInterval(() => {
      const nextNow = Date.now();
      setNow(nextNow);
      if (nextNow >= cooldownEndsAt) {
        globalThis.clearInterval(intervalId);
      }
    }, 1000);

    return () => globalThis.clearInterval(intervalId);
  }, [cooldownEndsAt]);

  const remainingSeconds = Math.max(
    0,
    Math.ceil((cooldownEndsAt - now) / 1000),
  );

  return (
    <Button
      color="link-color"
      size="sm"
      isLoading={isPending}
      isDisabled={isPending || remainingSeconds > 0}
      onClick={onClick}
    >
      {remainingSeconds > 0 ? (
        <Plural
          value={remainingSeconds}
          one="Resend available in # second"
          other="Resend available in # seconds"
        />
      ) : (
        <Trans>Resend email</Trans>
      )}
    </Button>
  );
}

function CheckEmailConfirmation({
  children,
  email,
}: {
  children: ReactNode;
  email: string;
}) {
  return (
    <div className="flex flex-col items-center text-center">
      <DecorativeCircles>
        <span className="text-display-sm leading-none">{'\u{1F4EB}'}</span>
      </DecorativeCircles>

      <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
        <Trans>Check your email</Trans>
      </h1>

      <p className="z-10 mt-3 text-md text-primary">
        <Trans>
          If you have an account with us at{' '}
          <span className="font-bold">{email}</span>, you&apos;ll receive a
          password reset email. The link will expire in 30 minutes.
        </Trans>
      </p>

      <p className="mt-8 mb-8 text-sm text-tertiary">
        <Trans>
          Double-check your spam or junk folder if you don&apos;t see the email
          within a few minutes.
        </Trans>
      </p>

      {children}

      <p className="mt-8 text-sm text-tertiary">
        <Link
          to="/login"
          className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
        >
          <Trans>Back to log in</Trans>
        </Link>
      </p>
    </div>
  );
}
